import { assign, assignWith } from "lodash";
export class ConfigurationManager {
    // input configs should be top-to-bottom, e.g. unit, investigation, problem
    constructor(defaults, configs) {
        Object.defineProperty(this, "defaults", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "configs", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.defaults = defaults;
        // reverse the array so searches are bottom-to-top, e.g. problem, investigation, unit
        this.configs = [...configs].reverse();
    }
    getProp(prop) {
        var _a;
        const found = (_a = this.configs.find(config => config[prop] != null)) === null || _a === void 0 ? void 0 : _a[prop];
        return (found != null ? found : this.defaults[prop]);
    }
    /*
     * UnitConfiguration properties
     */
    get appName() {
        return this.getProp("appName");
    }
    get pageTitle() {
        return this.getProp("pageTitle");
    }
    get demoProblemTitle() {
        return this.getProp("demoProblemTitle");
    }
    get defaultProblemOrdinal() {
        return this.getProp("defaultProblemOrdinal");
    }
    get showCommentTag() {
        return this.getProp("showCommentTag");
    }
    get showCommentRating() {
        return this.getProp("showCommentRating");
    }
    get commentTags() {
        return (this.showCommentTag) ? this.getProp("commentTags") : {};
    }
    get allowCustomCommentTags() {
        return this.showCommentTag ? this.getProp("allowCustomCommentTags") : false;
    }
    get aiEvaluation() {
        return this.getProp("aiEvaluation");
    }
    get aiPrompt() {
        return this.getProp("aiPrompt");
    }
    get chatTutorPrompts() {
        return this.getProp("chatTutorPrompts");
    }
    get chatTutorEnabled() {
        return this.getProp("chatTutorEnabled");
    }
    get chatTutorIntro() {
        return this.getProp("chatTutorIntro");
    }
    get autoAssignStudentsToIndividualGroups() {
        return this.getProp("autoAssignStudentsToIndividualGroups");
    }
    get defaultDocumentType() {
        return this.getProp("defaultDocumentType");
    }
    get defaultDocumentTitle() {
        return this.getProp("defaultDocumentTitle");
    }
    get docTimeStampPropertyName() {
        return this.getProp("docTimeStampPropertyName");
    }
    get docDisplayIdPropertyName() {
        return this.getProp("docDisplayIdPropertyName");
    }
    get defaultLearningLogTitle() {
        return this.getProp("defaultLearningLogTitle");
    }
    get initialLearningLogTitle() {
        return this.getProp("initialLearningLogTitle");
    }
    get defaultLearningLogDocument() {
        return this.getProp("defaultLearningLogDocument");
    }
    get autoSectionProblemDocuments() {
        return this.getProp("autoSectionProblemDocuments");
    }
    get documentLabelProperties() {
        return this.getProp("documentLabelProperties");
    }
    get documentLabels() {
        return this.getProp("documentLabels");
    }
    get disablePublish() {
        return this.getProp("disablePublish");
    }
    get enableHistoryRoles() {
        return this.getProp("enableHistoryRoles");
    }
    get enableCommentRoles() {
        return this.getProp("enableCommentRoles");
    }
    get copyPreferOriginTitle() {
        return this.getProp("copyPreferOriginTitle");
    }
    get disableTileDrags() {
        return this.getProp("disableTileDrags");
    }
    get showClassSwitcher() {
        return this.getProp("showClassSwitcher");
    }
    get supportStackedTwoUpView() {
        return this.getProp("supportStackedTwoUpView");
    }
    get showPublishedDocsInPrimaryWorkspace() {
        return this.getProp("showPublishedDocsInPrimaryWorkspace");
    }
    get comparisonPlaceholderContent() {
        return this.getProp("comparisonPlaceholderContent");
    }
    get initiallyHideExemplars() {
        return this.getProp("initiallyHideExemplars");
    }
    get navTabs() {
        return this.getProp("navTabs");
    }
    /*
     * ProblemConfiguration properties
     */
    get disabledFeatures() {
        // settings are merged rather than simply returning the closest non-empty value
        const reverseConfigs = [...this.configs].reverse();
        const mergedDisabled = {};
        mergeDisabledFeatures(mergedDisabled, this.defaults.disabledFeatures);
        for (const config of reverseConfigs) {
            mergeDisabledFeatures(mergedDisabled, config.disabledFeatures);
        }
        return Object.values(mergedDisabled);
    }
    get toolbar() {
        return this.getProp("toolbar");
    }
    get annotations() {
        return this.getProp("annotations");
    }
    get authorTools() {
        return this.getProp("authorTools");
    }
    get myResourcesToolbar() {
        return this.getProp("myResourcesToolbar");
    }
    get tools() {
        return this.getProp("tools");
    }
    get defaultDocumentTemplate() {
        return this.getProp("defaultDocumentTemplate");
    }
    get defaultDocumentTemplateEnabled() {
        return this.getProp("defaultDocumentTemplateEnabled");
    }
    get planningTemplate() {
        return this.getProp("planningTemplate");
    }
    get planningTemplateEnabled() {
        return this.getProp("planningTemplateEnabled");
    }
    get placeholder() {
        return this.getProp("placeholder");
    }
    get placeholderText() {
        return this.getProp("placeholderText");
    }
    get stamps() {
        return this.getProp("stamps");
    }
    get showIdeasButton() {
        return this.getProp("showIdeasButton");
    }
    get groupDocumentsEnabled() {
        return this.getProp("groupDocumentsEnabled");
    }
    get hide4up() {
        return this.getProp("hide4up");
    }
    get sortWorkConfig() {
        return this.getProp("sortWorkConfig");
    }
    get termOverrides() {
        return this.getProp("termOverrides");
    }
    get defaultPanelLayout() {
        return this.getProp("defaultPanelLayout");
    }
    get contentLayout() {
        return this.getProp("contentLayout");
    }
    get defaultSharedDocuments() {
        return this.getProp("defaultSharedDocuments");
    }
    get showShare() {
        return this.getProp("showShare");
    }
    get classWideDocuments() {
        return this.getProp("classWideDocuments");
    }
    get settings() {
        // settings are merged rather than simply returning the closest non-empty value
        // merge is just two levels deep: eg merges items under settings.table, but not elements of settings.table.tools
        const reverseSettings = [...this.configs].reverse().map(config => config.settings);
        return assignWith({}, this.defaults.settings, ...reverseSettings, (objValue, srcValue) => {
            return assign({}, objValue, srcValue);
        });
    }
}
export function mergeDisabledFeatures(disabled, disabledFeatures) {
    disabledFeatures === null || disabledFeatures === void 0 ? void 0 : disabledFeatures.forEach(feature => {
        const result = /^!?(.+)/.exec(feature);
        const featureKey = result === null || result === void 0 ? void 0 : result[1];
        featureKey && (disabled[featureKey] = feature);
    });
}
//# sourceMappingURL=data:application/json;base64,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